import { useState, useCallback, useEffect } from 'react';
import apiClient from '../api/client';
import useAuthToken from './useAuthToken';
import { useTemplates } from './useTemplates';
import { DateTime } from 'luxon';
import { convertLocalTimeToUTC, convertUTCToLocalTime } from '../utils/timeUtils';
import { TimeValue } from '../utils/TimeValue';
import { prepareTimeForAPI, processTimeFromAPI } from '../utils/timeApi';

// Interceptors for logging were moved to api/client.js to avoid re-declarations.

export const useAutomations = () => {
  const [automations, setAutomations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const token = useAuthToken();
  const templateHooks = useTemplates(); // Move the hook call to the top level

  // Improved helper function to transform audience format
  const transformAudienceFormat = useCallback((data) => {
    // Create a deep copy to avoid mutating the original data
    const apiData = JSON.parse(JSON.stringify(data));
    
    // Transform audience format if present
    if (apiData.audience) {
      // Ensure match_all is a boolean, not an array
      if (Array.isArray(apiData.audience.match_all)) {
        apiData.audience.match_all = true; // Default to true if it's an array
      }
      
      // If it's not a boolean already, convert it
      if (typeof apiData.audience.match_all !== 'boolean') {
        apiData.audience.match_all = Boolean(apiData.audience.match_all);
      }
      
      // Ensure conditions is an array
      if (!apiData.audience.conditions) {
        apiData.audience.conditions = [];
      }
      
      // If audience has groups property, transform it to the expected format
      if (apiData.audience.groups) {
        const formattedConditions = [];
        
        // Process all groups
        apiData.audience.groups.forEach(group => {
          if (!group.type || !Array.isArray(group.conditions)) return;
          
          group.conditions.forEach(condition => {
            if (!condition.recordType || !condition.fieldName || !condition.operator) return;
            
            formattedConditions.push({
              field: `${condition.recordType}.${condition.fieldName}`,
              operator: condition.operator,
              value: condition.value,
              condition_group: group.type === "ALL" ? "ALL" : "ANY"
            });
          });
        });
        
        // Replace with transformed format
        apiData.audience = {
          filter_name: apiData.audience.name || "Default Filter",
          match_all: true, // Ensure this is a boolean
          conditions: formattedConditions
        };
      }
    }
    
    return apiData;
  }, []);

  // Helper function to clean data before sending to API
  const cleanDataForAPI = useCallback((data) => {
    const cleanedData = { ...data };
    
    // Remove any undefined or null fields to prevent validation errors
    Object.keys(cleanedData).forEach(key => {
      if (cleanedData[key] === undefined || cleanedData[key] === null) {
        delete cleanedData[key];
      }
    });
    
    // If audience exists, ensure it has proper format
    if (cleanedData.audience) {
      // Ensure match_all is a boolean
      cleanedData.audience.match_all = 
        typeof cleanedData.audience.match_all === 'boolean' 
          ? cleanedData.audience.match_all 
          : true;
      
      // Ensure conditions is an array
      cleanedData.audience.conditions = 
        Array.isArray(cleanedData.audience.conditions) 
          ? cleanedData.audience.conditions 
          : [];
      
      // Clean up each condition
      cleanedData.audience.conditions = cleanedData.audience.conditions.map(cond => {
        // Ensure condition has all required fields
        return {
          field: cond.field || "",
          operator: cond.operator || "EQUALS",
          value: cond.value !== undefined ? cond.value : "",
          entity: cond.entity || cond.field?.split('.')[0] || "client"
        };
      });
    }
    
    return cleanedData;
  }, []);

  useEffect(() => {
    if (!token) {
      console.warn('No token for automations — skipping fetch');
      setIsLoading(false);
      return;
    }

    apiClient.get('/automations')
      .then(res => {
        // Enhanced debug logging of raw API response
        console.group('🔍 Raw Automation API Response');
        console.log('API Response Data:', res.data);
        console.groupEnd();
        
        // Transform received data from snake_case to camelCase
        const transformedAutomations = res.data.map(automation => {
          // Critical debug log for schedule_time
          console.group(`🔍 Processing automation "${automation.title}"`);
          console.log('Original schedule_time from API (UTC):', automation.schedule_time);
          
          // STEP 1: Convert UTC time from API to local time for display
          // Create a TimeValue object in UTC format
          const apiTimeValue = processTimeFromAPI(automation.schedule_time || '09:00:00');
          
          // Convert to local time for display
          const localTimeValue = apiTimeValue.toLocal();
          
          console.log(`🕒 Converted API time ${apiTimeValue.value} (UTC) to local time: ${localTimeValue.value}`);
          console.log('Final scheduleTime value for display:', localTimeValue.value);
          console.groupEnd();
          
          return {
            id: automation.id,
            title: automation.title,
            status: automation.status,
            emailSubject: automation.email_subject,
            scheduleType: automation.schedule_type,
            scheduleInterval: automation.schedule_interval ? Number(automation.schedule_interval) : 1,
            // Store the converted local TimeValue object
            scheduleTime: localTimeValue,
            scheduleDay: automation.schedule_day ? Number(automation.schedule_day) : 1,
            scheduleDays: Array.isArray(automation.schedule_days) ? automation.schedule_days : [],
            emailFromId: automation.email_sender_id,
            complex_filter_slug: automation.complex_filter_slug,
            createdAt: automation.created_at,
            updatedAt: automation.updated_at,
            // Execution details from the enhanced API
            executionCount: automation.execution_count || 0,
            lastRun: automation.last_executed || null,
            lastExecutionStatus: automation.last_execution_status || null,
            recipientCount: automation.recipient_count || 0,
            audiencePreviewCount: automation.audience_preview_count || null
          };
        });
        
        setAutomations(transformedAutomations);
      })
      .catch(err => {
        console.error('Failed to load automations:', err);
        setError(err);
      })
      .finally(() => setIsLoading(false));
  }, [token]);

  const createAutomation = useCallback(async (data) => {
    try {
      setIsLoading(true);
      console.group('🚀 Creating automation');
      console.log('Raw input data:', data);
      
      // Transform the payload to ensure it matches the expected API format
      const cleanedPayload = {
        title: data.title,
        email_subject: data.email_subject,
        email_sender_id: data.email_sender_id,
        schedule_type: data.schedule_type,
        schedule_interval: Number(data.schedule_interval || 1),
        schedule_days: Array.isArray(data.schedule_days) ? data.schedule_days : [],
        schedule_day: data.schedule_day ? String(data.schedule_day) : '1',
        template_id: data.template_id,
        prevent_duplicates: Boolean(data.prevent_duplicates),
        status: data.status || 'DRAFT'
      };
      
      // ✅ CRITICAL FIX: Handle schedule_time like updateAutomation does to prevent triple conversion
      if (data.scheduleTime instanceof TimeValue) {
        // TimeValue object - convert directly to UTC string (no prepareTimeForAPI)
        console.log('🕒 createAutomation: data.scheduleTime is a TimeValue object:', data.scheduleTime);
        cleanedPayload.schedule_time = data.scheduleTime.toUTC().value;
        console.log(`🕒 createAutomation: Prepared schedule_time from TimeValue for API: ${cleanedPayload.schedule_time}`);
      } else if (typeof data.scheduleTime === 'string') {
        // String - assume it's already UTC (from Edit.js or other sources)
        console.log(`🕒 createAutomation: data.scheduleTime is a string: "${data.scheduleTime}". Passing directly as UTC.`);
        cleanedPayload.schedule_time = data.scheduleTime;
      } else if (data.schedule_time) {
        // Fallback to schedule_time field
        console.log(`🕒 createAutomation: Using fallback schedule_time: "${data.schedule_time}"`);
        cleanedPayload.schedule_time = data.schedule_time;
      } else {
        // Default fallback
        console.log('🕒 createAutomation: No schedule time provided, using default 09:00:00');
        cleanedPayload.schedule_time = '09:00:00';
      }
      
      // CRITICAL FIX: Handle all audience formats properly
      if (data.complex_filter_slug) {
        cleanedPayload.complex_filter_slug = data.complex_filter_slug;
        console.log(`Using complex filter: ${cleanedPayload.complex_filter_slug}`);
      } else if (data.audience_id) {
        cleanedPayload.audience_id = data.audience_id;
        console.log(`Using existing audience ID: ${cleanedPayload.audience_id}`);
      } else if (data.audience) {
        // Handle new audience object format from Create.js
        cleanedPayload.audience = data.audience;
        console.log(`Using new audience object:`, cleanedPayload.audience);
      }
      
      // Log details
      console.log('Prepared schedule_time:', cleanedPayload.schedule_time);
      
      // CRITICAL FIX: Only process schedule_day if it actually exists in the payload AND we're monthly
      if (cleanedPayload.schedule_type === 'MONTHLY' && cleanedPayload.schedule_day !== undefined) {
        // Convert to integer for backend validation
        cleanedPayload.schedule_day = parseInt(cleanedPayload.schedule_day) || 1;
        console.log(`🔧 Converted schedule_day to integer for MONTHLY: ${cleanedPayload.schedule_day}`);
      } else {
        // Remove schedule_day for non-monthly schedules
        delete cleanedPayload.schedule_day;
        console.log('🔧 Removed schedule_day for non-MONTHLY schedule');
      }
      
      // Log the ENTIRE payload as JSON for debugging
      console.log('🔍 FULL PAYLOAD JSON:', JSON.stringify(cleanedPayload, null, 2));
      
      // Double verify entire payload for number values that should be strings  
      Object.entries(cleanedPayload).forEach(([key, value]) => {
        console.log(`${key}: ${value} (${typeof value})`);
      });
      
      // Remove any undefined or null fields
      Object.keys(cleanedPayload).forEach(key => {
        if (cleanedPayload[key] === undefined || cleanedPayload[key] === null) {
          delete cleanedPayload[key];
        }
      });
      
      // Double verify entire payload after cleanup
      Object.entries(cleanedPayload).forEach(([key, value]) => {
        console.log(`FINAL ${key}: ${value} (${typeof value})`);
      });
      
      const response = await apiClient.post('/automations', cleanedPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Verify what was sent vs what was returned
      console.log('POST-SAVE VERIFICATION:', {
        schedule_time: response.data.schedule_time,
        next_scheduled_at: response.data.next_scheduled_at
      });
      
      console.log('Automation created successfully:', response.data);
      setAutomations(prev => [...prev, response.data]);
      console.groupEnd();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Automation creation failed:', err);
      console.log('Error response:', err.response);
      if (err.response?.data?.detail) {
        console.log('Validation errors:', err.response.data.detail);
      }
      console.groupEnd();
      return { success: false, error: err };
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const getAutomation = useCallback(async (id) => {
    try {
      setIsLoading(true);
      console.log(`Fetching automation ${id}...`);
      const res = await apiClient.get(`/automations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data;
    } catch (err) {
      console.group(`❌ Error fetching automation ${id}`);
      console.error('Error:', err);
      if (err.response) {
        console.error('Error data:', err.response.data);
      }
      console.groupEnd();
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const updateAutomation = useCallback(async (id, data) => {
    try {
      setIsUpdating(true);
      console.group(`📝 Updating automation ${id}`);
      console.log('Original data:', data);
      
      // Transform the audience format for API compatibility
      const transformedData = transformAudienceFormat(data);
      const apiData = cleanDataForAPI(transformedData);
      
      // Remove audience from the main automation update to avoid duplication
      // since we've already processed it separately above
      if (apiData.audience) {
        console.log('Removing audience data from main automation update to prevent duplication');
        delete apiData.audience;
      }
      
      // Handle schedule_time explicitly with TimeValue
      if (data.scheduleTime instanceof TimeValue) {
        // This case handles if Edit.js ever sends a TimeValue object directly.
        console.log('🕒 updateAutomation: data.scheduleTime is a TimeValue object:', data.scheduleTime);
        apiData.schedule_time = data.scheduleTime.toUTC().value; // Convert TimeValue to UTC string
        console.log(`🕒 updateAutomation: Prepared schedule_time from TimeValue for API: ${apiData.schedule_time}`);
      } else if (typeof data.scheduleTime === 'string') {
        // This case handles if Edit.js sends a string.
        // We TRUST that this string from Edit.js is ALREADY the correct UTC string.
        console.log(`🕒 updateAutomation: data.scheduleTime is a string: "${data.scheduleTime}". Passing it directly as UTC.`);
        apiData.schedule_time = data.scheduleTime; // <<< KEY CHANGE: NO prepareTimeForAPI() here
      } else if (apiData.schedule_time && typeof apiData.schedule_time === 'string' && !(data.scheduleTime)) {
        // Fallback: if data.scheduleTime wasn't the source but apiData.schedule_time is a string from elsewhere (e.g. cleaning functions)
        // This also assumes it's the correct UTC string.
        console.log(`🕒 updateAutomation: apiData.schedule_time is a string: "${apiData.schedule_time}". Passing it directly as UTC.`);
        apiData.schedule_time = apiData.schedule_time; // Pass through
      }
      
      // Double-check the entire payload for any missed TimeValue objects (defensive coding)
      Object.keys(apiData).forEach(key => {
        const value = apiData[key];
        if (value instanceof TimeValue) {
          console.log(`🚨 Found unexpected TimeValue in ${key}, converting to UTC string`);
          apiData[key] = value.toUTC().value;
        }
      });
      
      // Final critical check for schedule_day type IMMEDIATELY before sending
      if (apiData.schedule_day !== undefined) {
        // Try a simpler approach - just a plain string without prefix
        apiData.schedule_day = String(apiData.schedule_day || "1");
        console.log(`🔧 Using simple string for schedule_day: ${apiData.schedule_day}`);
      }
      
      // Check if we have a complex_filter_slug and make sure it's properly formatted
      if (apiData.complex_filter_slug) {
        console.log(`Using complex filter: ${apiData.complex_filter_slug}`);
      }
      
      // Log the ENTIRE payload as JSON for debugging
      console.log('🔍 FULL API PAYLOAD:', JSON.stringify(apiData, null, 2));
      console.log('Sending update to API...');
      
      try {
        // Clone the data to avoid modifying the original
        const postData = {...apiData};
        
        // Handle schedule_day parameter separately if needed
        if (postData.schedule_day !== undefined) {
          // Remove it from the object
          delete postData.schedule_day;
          
          // Add it back as a URL parameter instead
          const scheduleDay = String(apiData.schedule_day || "1");
          console.log(`🚨 EMERGENCY FIX: Sending schedule_day as URL param: ${scheduleDay}`);
          
          // Make request with the schedule_day as a URL parameter
          const res = await apiClient.patch(
            `/automations/${id}?schedule_day=${encodeURIComponent(scheduleDay)}`, 
            postData, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Verify what was sent vs what was returned
          console.log('POST-SAVE VERIFICATION:', {
            schedule_time: res.data.schedule_time,
            next_scheduled_at: res.data.next_scheduled_at
          });
          
          console.log('Update successful:', res.data);
          console.groupEnd();
          
          // Update local state with converted result
          const updatedAutomation = {
            ...data,
            // Keep the TimeValue but update with the latest API data
            scheduleTime: data.scheduleTime instanceof TimeValue 
              ? data.scheduleTime 
              : processTimeFromAPI(res.data.schedule_time).toLocal()
          };
          
          setAutomations(prev => prev.map(auto => auto.id === id ? { ...auto, ...updatedAutomation } : auto));
          return { success: true, data: res.data };
        } else {
          // Regular path when schedule_day isn't present
          const res = await apiClient.patch(`/automations/${id}`, apiData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Verify what was sent vs what was returned
          console.log('POST-SAVE VERIFICATION:', {
            schedule_time: res.data.schedule_time,
            next_scheduled_at: res.data.next_scheduled_at
          });
          
          console.log('Update successful:', res.data);
          console.groupEnd();
          
          // Update local state with converted result
          const updatedAutomation = {
            ...data,
            // Keep the TimeValue but update with the latest API data
            scheduleTime: data.scheduleTime instanceof TimeValue 
              ? data.scheduleTime 
              : processTimeFromAPI(res.data.schedule_time).toLocal()
          };
          
          setAutomations(prev => prev.map(auto => auto.id === id ? { ...auto, ...updatedAutomation } : auto));
          return { success: true, data: res.data };
        }
      } catch (err) {
        console.group(`❌ Error updating automation ${id}`);
        console.error('Error:', err);
        
        // Enhanced error logging
        if (err.response) {
          console.error('Error status:', err.response.status);
          console.error('Error data:', err.response.data);
          console.error('Error headers:', err.response.headers);
        }
        console.groupEnd();
        
        return { success: false, error: err };
      } finally {
        setIsUpdating(false);
      }
    } catch (err) {
      console.group(`❌ Error updating automation ${id}`);
      console.error('Error:', err);
      
      // Enhanced error logging
      if (err.response) {
        console.error('Error status:', err.response.status);
        console.error('Error data:', err.response.data);
        console.error('Error headers:', err.response.headers);
      }
      console.groupEnd();
      
      return { success: false, error: err };
    }
  }, [token, transformAudienceFormat, cleanDataForAPI]);

  const deleteAutomation = useCallback(async (id) => {
    try {
      setIsLoading(true);
      await apiClient.delete(`/automations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutomations(prev => prev.filter(auto => auto.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, error: err };
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const toggleAutomation = useCallback(async (id) => {
    const target = automations.find(a => a.id === id);
    const newStatus = target?.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    
    try {
      setIsUpdating(true);
      const res = await apiClient.patch(`/automations/${id}/toggle`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Automation status toggled successfully:', res.data);
      setAutomations(prev => prev.map(auto => auto.id === id ? { ...auto, status: newStatus } : auto));
      return { success: true, data: res.data };
    } catch (err) {
      console.group(`❌ Error toggling automation ${id}`);
      console.error('Error:', err);
      if (err.response) {
        console.error('Error data:', err.response.data);
      }
      console.groupEnd();
      return { success: false, error: err };
    } finally {
      setIsUpdating(false);
    }
  }, [automations, token]);

  const cloneAutomation = useCallback(async (id) => {
    try {
      setIsLoading(true);
      // Use the dedicated clone endpoint
      const res = await apiClient.post(`/automations/${id}/clone`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Automation cloned successfully:', res.data);
      // Add the new cloned automation to the list
      setAutomations(prev => [...prev, res.data]);
      return { success: true, data: res.data };
    } catch (err) {
      console.error('Automation cloning error:', err);
      
      // Enhanced error logging
      if (err.response) {
        console.error('Error status:', err.response.status);
        console.error('Error data:', err.response.data);
        console.error('Error headers:', err.response.headers);
      }
      
      return { success: false, error: err };
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  return {
    automations,
    isLoading,
    isUpdating,
    error,
    createAutomation,
    updateAutomation,
    deleteAutomation,
    toggleAutomation,
    cloneAutomation,
    getAutomation
  };
};

export const useAutomation = (id) => {
  const { automations } = useAutomations();
  const automation = automations.find(auto => auto.id === id) || null;

  return {
    automation,
    stats: automation?.stats || {},
    isLoading: false,
    error: null
  };
};